import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue'

import { ExecuteInsightFlow } from '../flows/ExecuteInsightFlow'
import type { ExecutePayloadInput } from '../models/execute.types'
import type { ExecuteInsightOutcome, InsightPreviewState } from '../models/workbench.types'

export interface UseInsightPreviewOptions {
  accountId: MaybeRefOrGetter<number | undefined>
  workspaceId: MaybeRefOrGetter<number | undefined>
  currentSignature: MaybeRefOrGetter<string>
  flow?: ExecuteInsightFlow
}

export interface UseInsightPreview {
  state: ComputedRef<InsightPreviewState>
  canPersist: ComputedRef<boolean>
  lastOutcome: ComputedRef<ExecuteInsightOutcome | null>
  runDryRun: (payload: ExecutePayloadInput) => Promise<ExecuteInsightOutcome>
  persist: (payload: ExecutePayloadInput) => Promise<ExecuteInsightOutcome>
  reset: () => void
  cancel: () => void
}

export function useInsightPreview(options: UseInsightPreviewOptions): UseInsightPreview {
  const flow = options.flow ?? new ExecuteInsightFlow()
  const state = ref<InsightPreviewState>({ kind: 'idle' })
  const lastOutcome = ref<ExecuteInsightOutcome | null>(null)
  let generation = 0
  let controller: AbortController | null = null

  const canPersist = computed(
    () =>
      state.value.kind === 'preview_ready' &&
      state.value.requestSignature === toValue(options.currentSignature),
  )

  function cancel(): void {
    controller?.abort()
  }

  function reset(): void {
    generation += 1
    cancel()
    controller = null
    state.value = { kind: 'idle' }
    lastOutcome.value = null
  }

  async function execute(
    payload: ExecutePayloadInput,
    previewSignature?: string,
  ): Promise<ExecuteInsightOutcome> {
    const accountId = toValue(options.accountId)
    const workspaceId = toValue(options.workspaceId)
    if (accountId === undefined || workspaceId === undefined) {
      const outcome: ExecuteInsightOutcome = {
        kind: 'invalid',
        errorKey: 'insights.sysadmin.execute.configRequired',
      }
      lastOutcome.value = outcome
      return outcome
    }

    const request = ++generation
    controller?.abort()
    const activeController = new AbortController()
    controller = activeController
    const previousState = state.value
    state.value = { kind: 'loading' }

    const outcome = await flow.execute({
      accountId,
      workspaceId,
      payload,
      ...(previewSignature ? { previewSignature } : {}),
      signal: activeController.signal,
    })
    if (request !== generation) return { kind: 'canceled' }
    if (activeController.signal.aborted) {
      state.value = previousState
      return { kind: 'canceled' }
    }
    lastOutcome.value = outcome

    switch (outcome.kind) {
      case 'preview_ready':
        state.value = {
          kind: 'preview_ready',
          insight: outcome.insight,
          requestSignature: outcome.requestSignature,
        }
        break
      case 'skipped':
        state.value = { kind: 'skipped', message: outcome.message }
        break
      case 'error':
        state.value = { kind: 'error', error: outcome.error }
        break
      case 'canceled':
        state.value = previousState
        break
      case 'invalid':
      case 'blocked':
      case 'created':
        state.value = previousState
        break
    }
    return outcome
  }

  function runDryRun(payload: ExecutePayloadInput): Promise<ExecuteInsightOutcome> {
    return execute({ ...payload, dry_run: true })
  }

  function persist(payload: ExecutePayloadInput): Promise<ExecuteInsightOutcome> {
    const previewSignature =
      state.value.kind === 'preview_ready' ? state.value.requestSignature : ''
    return execute({ ...payload, dry_run: false }, previewSignature)
  }

  onScopeDispose(reset)

  return {
    state: computed(() => state.value),
    canPersist,
    lastOutcome: computed(() => lastOutcome.value),
    runDryRun,
    persist,
    reset,
    cancel,
  }
}
